import React, { useCallback, useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import Colors from "../../../../Resources/Color";
import HoverableTouchableOpacity from "../../../Components/HoverableTouchableOpacity";
import { NutritionPlanMealCard } from "./NutritionPlanMealCard";
import {
  getNutritionPlanMealSortableId,
  NUTRITION_PLAN_DRAG_TYPE_MEAL,
} from "./nutritionPlanDragUtils";
import {
  getNutritionPlanMealUiState,
  getNutritionPlanOptionUiState,
} from "./nutritionPlanEditorSession";
import { useNutritionPlanFoodsDragController } from "./useNutritionPlanFoodsDragController";
import { isNutritionPlanMealFoodPickerOpen } from "./nutritionPlanFoodPickerScopeUtils";

function getResolvedSelectedOptionId(meal, selectedOptionId) {
  if (
    Array.isArray(meal?.options) &&
    meal.options.some((option) => option?.id === selectedOptionId)
  ) {
    return selectedOptionId;
  }

  return meal?.options?.[0]?.id ?? "";
}

function SortableMealCard({
  coach,
  client,
  t,
  language,
  meal,
  mealUiState,
  onMealUiStateChange,
  openInlinePickerOwner,
  foodPickerOpen,
  onFoodPickerOpenChange,
  selectedOptionId,
  onSelectOption,
  selectedOptionUiState,
  onSelectedOptionUiStateChange,
  onInlinePickerOpenChange,
  forceCollapsedForMealDrag,
  compactLayout,
  sensors,
  updateMealName,
  requestRemoveMeal,
  duplicateMeal,
  addOption,
  reorderOptions,
  updateOptionName,
  removeOption,
  hasCopiedOptionFoods,
  onCopyOptionFoods,
  onPasteOptionFoods,
  onAddFood,
  updateItemQuantity,
  updateItemName,
  updateItemUnit,
  removeItem,
  updateEquivalenceQuantity,
  updateEquivalenceName,
  updateEquivalenceUnit,
  toggleEquivalenceQuantityMode,
  removeEquivalence,
  updateMealNotes,
  isFoodDragActive,
  activeFoodDragMealId,
  activeFoodDragItemId,
  foodDragTarget,
  foodDragPlaceholderHeight,
  registerFoodOptionBodyNode,
  registerFoodRowNode,
  getFoodDragHandleProps,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({
      id: getNutritionPlanMealSortableId(meal.id),
      data: {
        type: NUTRITION_PLAN_DRAG_TYPE_MEAL,
        mealId: meal.id,
      },
      disabled: isFoodDragActive,
    });

  const mealDragTransform = CSS.Transform.toString(
    transform
      ? {
          ...transform,
          scaleX: 1,
          scaleY: 1,
        }
      : null,
  );

  return (
    <View
      ref={setNodeRef}
      style={[
        styles.mealListItem,
        activeFoodDragMealId === meal.id ? styles.mealListItemFoodDragSource : null,
        isDragging ? styles.mealListItemDragging : null,
        {
          transform: mealDragTransform,
          transition,
          opacity: isDragging ? 0.7 : 1,
        },
      ]}
    >
      <View pointerEvents="none" style={styles.mealListItemFrame} />
      <View style={styles.mealListItemContent}>
        <NutritionPlanMealCard
          coach={coach}
          client={client}
          t={t}
          language={language}
          meal={meal}
          mealUiState={mealUiState}
          onMealUiStateChange={onMealUiStateChange}
          openInlinePickerOwner={openInlinePickerOwner}
          foodPickerOpen={foodPickerOpen}
          onFoodPickerOpenChange={onFoodPickerOpenChange}
          selectedOptionId={selectedOptionId}
          onSelectOption={onSelectOption}
          selectedOptionUiState={selectedOptionUiState}
          onSelectedOptionUiStateChange={onSelectedOptionUiStateChange}
          onInlinePickerOpenChange={onInlinePickerOpenChange}
          forceCollapsedForMealDrag={forceCollapsedForMealDrag}
          compactLayout={compactLayout}
          sensors={sensors}
          dragHandleProps={{ attributes, listeners, isDragging }}
          updateMealName={updateMealName}
          updateMealNotes={updateMealNotes}
          requestRemoveMeal={requestRemoveMeal}
          duplicateMeal={duplicateMeal}
          addOption={addOption}
          reorderOptions={reorderOptions}
          updateOptionName={updateOptionName}
          removeOption={removeOption}
          hasCopiedOptionFoods={hasCopiedOptionFoods}
          onCopyOptionFoods={onCopyOptionFoods}
          onPasteOptionFoods={onPasteOptionFoods}
          onAddFood={onAddFood}
          isFoodDragActive={isFoodDragActive}
          activeFoodDragItemId={activeFoodDragItemId}
          foodDragTarget={foodDragTarget}
          foodDragPlaceholderHeight={foodDragPlaceholderHeight}
          registerFoodOptionBodyNode={registerFoodOptionBodyNode}
          registerFoodRowNode={registerFoodRowNode}
          getFoodDragHandleProps={getFoodDragHandleProps}
          updateItemQuantity={updateItemQuantity}
          updateItemName={updateItemName}
          updateItemUnit={updateItemUnit}
          removeItem={removeItem}
          updateEquivalenceQuantity={updateEquivalenceQuantity}
          updateEquivalenceName={updateEquivalenceName}
          updateEquivalenceUnit={updateEquivalenceUnit}
          toggleEquivalenceQuantityMode={toggleEquivalenceQuantityMode}
          removeEquivalence={removeEquivalence}
        />
      </View>
    </View>
  );
}

const MemoSortableMealCard = React.memo(SortableMealCard);

export function NutritionPlanMealsSection({
  coach,
  client = null,
  t,
  language,
  meals,
  openInlinePickerOwner = null,
  onInlinePickerOpenChange,
  selectedMealOptionIds,
  onSelectMealOption,
  mealUiById,
  optionUiById,
  onUpdateMealUiState,
  onUpdateOptionUiState,
  compactLayout,
  sensors,
  editorScrollRef,
  addMeal,
  reorderMeals,
  updateMealName,
  requestRemoveMeal,
  duplicateMeal,
  addOption,
  reorderOptions,
  updateOptionName,
  removeOption,
  hasCopiedOptionFoods = false,
  onCopyOptionFoods,
  onPasteOptionFoods,
  onAddFood,
  updateItemQuantity,
  updateItemName,
  updateItemUnit,
  removeItem,
  updateEquivalenceQuantity,
  updateEquivalenceName,
  updateEquivalenceUnit,
  toggleEquivalenceQuantityMode,
  removeEquivalence,
  updateMealNotes,
  moveOptionItem,
  isLoading = false,
}) {
  const [activeMealDragId, setActiveMealDragId] = useState(null);
  const {
    isFoodDragActive,
    activeFoodDragMealId,
    activeFoodDragItemId,
    foodDragTarget,
    foodDragPlaceholderHeight,
    frozenSelectedOptionIds,
    registerFoodOptionBodyNode,
    registerFoodRowNode,
    getFoodDragHandleProps,
    cancelFoodDrag,
  } = useNutritionPlanFoodsDragController({
    meals,
    selectedMealOptionIds,
    editorScrollRef,
    moveOptionItem,
  });

  const collisionDetection = useCallback((args) => {
    const activeType = args?.active?.data?.current?.type;
    if (activeType !== NUTRITION_PLAN_DRAG_TYPE_MEAL) {
      return [];
    }

    const mealDroppables = args.droppableContainers.filter(
      (container) => container?.data?.current?.type === NUTRITION_PLAN_DRAG_TYPE_MEAL,
    );

    return closestCenter({
      ...args,
      droppableContainers: mealDroppables,
    });
  }, []);

  const handleDragStart = useCallback(
    (event) => {
      const activeData = event?.active?.data?.current;

      if (activeData?.type === NUTRITION_PLAN_DRAG_TYPE_MEAL) {
        setActiveMealDragId(String(activeData?.mealId ?? "").trim() || null);
        return;
      }
    },
    [],
  );

  const handleDragEnd = useCallback(
    (event) => {
      setActiveMealDragId(null);

      const activeData = event?.active?.data?.current;

      const { active, over } = event;
      const overData = over?.data?.current;

      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = meals.findIndex((meal) => meal.id === activeData?.mealId);
      const newIndex = meals.findIndex((meal) => meal.id === overData?.mealId);
      if (oldIndex < 0 || newIndex < 0) {
        return;
      }

      reorderMeals(arrayMove(meals, oldIndex, newIndex));
    },
    [meals, reorderMeals],
  );

  const renderedSelectedOptionIds =
    isFoodDragActive && frozenSelectedOptionIds
      ? frozenSelectedOptionIds
      : selectedMealOptionIds;

  return (
    <View style={styles.container}>
      {isLoading ? (
        <View style={styles.loadingState}>
          {[0, 1, 2].map((index) => (
            <View key={`meal-loading-${index}`} style={styles.loadingCard}>
              <View style={styles.loadingCardHeader}>
                <View style={[styles.loadingLine, styles.loadingTitleLine]} />
                <View style={[styles.loadingLine, styles.loadingMetaLine]} />
              </View>
              <View style={styles.loadingBody}>
                <View style={[styles.loadingLine, styles.loadingBlockLine]} />
                <View style={[styles.loadingLine, styles.loadingBlockLineShort]} />
                <View style={[styles.loadingLine, styles.loadingBlockLine]} />
              </View>
            </View>
          ))}
        </View>
      ) : meals.length === 0 ? (
        <View style={styles.emptyState}>
          <Ionicons name="restaurant-outline" size={20} color={Colors.grayColor} />
          <Text style={styles.emptyStateTitle}>
            {t("nutritionPlanMealsEmptyTitle-string", "No meals yet")}
          </Text>
          <Text style={styles.emptyStateCopy}>
            {t(
              "nutritionPlanMealsEmptyDescription-string",
              "Create the first meal to start composing the plan.",
            )}
          </Text>
        </View>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetection}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={() => {
            setActiveMealDragId(null);
            cancelFoodDrag();
          }}
        >
          <SortableContext
            items={meals.map((meal) => getNutritionPlanMealSortableId(meal.id))}
            strategy={verticalListSortingStrategy}
          >
            <View style={styles.mealList}>
              {meals.map((meal) => {
                const selectedOptionId = getResolvedSelectedOptionId(
                  meal,
                  renderedSelectedOptionIds?.[meal.id] ?? "",
                );
                return (
                  <MemoSortableMealCard
                    key={meal.id}
                    coach={coach}
                    client={client}
                    t={t}
                    language={language}
                    meal={meal}
                    openInlinePickerOwner={openInlinePickerOwner}
                    foodPickerOpen={isNutritionPlanMealFoodPickerOpen({
                      openInlinePickerOwner,
                      mealId: meal.id,
                    })}
                    onFoodPickerOpenChange={(nextOpen) =>
                      onInlinePickerOpenChange?.(
                        {
                          mealId: meal.id,
                          kind: "main",
                        },
                        nextOpen,
                      )
                    }
                    selectedOptionId={selectedOptionId}
                    onSelectOption={(optionId) => {
                      if (isFoodDragActive) {
                        return;
                      }

                      onSelectMealOption?.(meal.id, optionId);
                    }}
                    mealUiState={getNutritionPlanMealUiState(mealUiById, meal)}
                    onMealUiStateChange={(updater) =>
                      onUpdateMealUiState?.(meal, updater)
                    }
                    selectedOptionUiState={getNutritionPlanOptionUiState(
                      optionUiById,
                      meal.id,
                      selectedOptionId,
                    )}
                    onSelectedOptionUiStateChange={(updater) =>
                      onUpdateOptionUiState?.(meal.id, selectedOptionId, updater)
                    }
                    onInlinePickerOpenChange={onInlinePickerOpenChange}
                    forceCollapsedForMealDrag={activeMealDragId != null}
                    compactLayout={compactLayout}
                    sensors={sensors}
                    updateMealName={updateMealName}
                    requestRemoveMeal={requestRemoveMeal}
                    duplicateMeal={duplicateMeal}
                    addOption={addOption}
                    reorderOptions={reorderOptions}
                    updateOptionName={updateOptionName}
                    removeOption={removeOption}
                    hasCopiedOptionFoods={hasCopiedOptionFoods}
                    onCopyOptionFoods={onCopyOptionFoods}
                    onPasteOptionFoods={onPasteOptionFoods}
                    onAddFood={onAddFood}
                    updateItemQuantity={updateItemQuantity}
                    updateItemName={updateItemName}
                    updateItemUnit={updateItemUnit}
                    removeItem={removeItem}
                    updateEquivalenceQuantity={updateEquivalenceQuantity}
                    updateEquivalenceName={updateEquivalenceName}
                    updateEquivalenceUnit={updateEquivalenceUnit}
                    toggleEquivalenceQuantityMode={toggleEquivalenceQuantityMode}
                    removeEquivalence={removeEquivalence}
                    updateMealNotes={updateMealNotes}
                    isFoodDragActive={isFoodDragActive}
                    activeFoodDragMealId={activeFoodDragMealId}
                    activeFoodDragItemId={activeFoodDragItemId}
                    foodDragTarget={foodDragTarget}
                    foodDragPlaceholderHeight={foodDragPlaceholderHeight}
                    registerFoodOptionBodyNode={registerFoodOptionBodyNode}
                    registerFoodRowNode={registerFoodRowNode}
                    getFoodDragHandleProps={getFoodDragHandleProps}
                  />
                );
              })}
            </View>
          </SortableContext>
        </DndContext>
      )}

      <HoverableTouchableOpacity
        activeOpacity={0.85}
        disabled={isLoading}
        onPress={addMeal}
        style={[
          styles.addMealButton,
          isLoading ? styles.addMealButtonDisabled : null,
        ]}
        hoverStyle={isLoading ? null : styles.addMealButtonHover}
      >
        <Ionicons name="add-outline" size={15} color={Colors.bubbleColor} />
        <Text style={styles.addMealButtonText}>
          {t("nutritionPlanAddMeal-string", "Add meal")}
        </Text>
      </HoverableTouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 14,
  },
  loadingState: {
    gap: 16,
  },
  loadingCard: {
    borderRadius: 14,
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
    backgroundColor: Colors.whiteColor,
    padding: 16,
    gap: 16,
  },
  loadingCardHeader: {
    gap: 10,
  },
  loadingBody: {
    gap: 10,
  },
  loadingLine: {
    borderRadius: 999,
    backgroundColor: Colors.lightGrayColor,
  },
  loadingTitleLine: {
    height: 14,
    width: "38%",
  },
  loadingMetaLine: {
    height: 10,
    width: "18%",
  },
  loadingBlockLine: {
    height: 12,
    width: "100%",
  },
  loadingBlockLineShort: {
    height: 12,
    width: "72%",
  },
  addMealButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    alignSelf: "center",
    minHeight: 34,
    paddingVertical: 6,
    paddingHorizontal: 10,
  },
  addMealButtonHover: {
    opacity: 0.76,
  },
  addMealButtonDisabled: {
    opacity: 0.56,
  },
  addMealButtonText: {
    fontSize: 12,
    fontWeight: "600",
    color: Colors.bubbleColor,
  },
  emptyState: {
    borderRadius: 12,
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
    borderStyle: "dashed",
    paddingVertical: 24,
    paddingHorizontal: 18,
    alignItems: "center",
  },
  emptyStateTitle: {
    marginTop: 10,
    fontSize: 13,
    fontWeight: "600",
    color: Colors.blackColor,
  },
  emptyStateCopy: {
    marginTop: 6,
    maxWidth: 360,
    textAlign: "center",
    fontSize: 12,
    lineHeight: 18,
    color: Colors.grayColor,
  },
  mealList: {
    gap: 16,
  },
  mealListItem: {
    borderRadius: 14,
    backgroundColor: "transparent",
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
    position: "relative",
    overflow: "hidden",
    zIndex: 1,
  },
  mealListItemFrame: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: 14,
    backgroundColor: "transparent",
    zIndex: 0,
  },
  mealListItemContent: {
    position: "relative",
    zIndex: 1,
    borderRadius: 14,
    backgroundColor: Colors.whiteColor,
    overflow: "hidden",
  },
  mealListItemFoodDragSource: {
    overflow: "visible",
    zIndex: 5000,
    elevation: 5000,
  },
  mealListItemDragging: {
    zIndex: 20,
    elevation: 20,
  },
});
